/* eslint-disable camelcase */
import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'
import { InfoButton, LinkButton } from 'components/buttons'
import { MadLibInputs, InputConfig } from '../MadLibInputs/MadLibInputs'
import Slide from '../slide/slide'

interface WishlistPreferencesProps {
  firstName: string
  nextPath: string
  onSubmit: (data: Record<string, any>) => void
  initialValues: {
    preferences: { name: string }[]
    favorite_color: string
  }
}

const inputsProperties: Record<string, InputConfig> = {
  'preferences[0]': {
    type: 'text',
    helpText: 'ex: dinosaurs',
    affinity: 'loves',
  },
  'preferences[1]': {
    type: 'text',
    helpText: 'ex: Dora the Explorer',
    affinity: 'loves',
  },
  'preferences[2]': {
    type: 'text',
    helpText: 'ex: new clothes',
    affinity: 'needs',
  },
  favorite_color: { type: 'colorpicker', helpText: 'ex: rainbow' },
  'preferences[3]': {
    type: 'text',
    helpText: 'ex: loud toys',
    affinity: 'please_no',
  },
}

interface FormValues {
  preferences?: { affinity?: string; name?: string }[]
  favorite_color?: string
}

export const WishlistPreferences = ({
  firstName,
  nextPath,
  onSubmit,
  initialValues: { preferences, favorite_color },
}: WishlistPreferencesProps) => {
  const navigate = useNavigate()
  const [formValues, setFormValues] = useState<FormValues>({})

  const handleValuesChange = useCallback((values: Record<string, any>) => {
    const newPreferences = Object.entries(values)
      .filter(([key]) => key.startsWith('preferences'))
      .map(([, value], index) => ({
        affinity: inputsProperties[`preferences[${index}]`].affinity,
        name: value,
      }))

    setFormValues({
      preferences: newPreferences,
      favorite_color: values.favorite_color,
    })
  }, [])

  const handleNext = useCallback(() => {
    onSubmit(formValues)
    navigate(nextPath)
  }, [formValues, onSubmit, nextPath])

  const handleSkip = useCallback(() => {
    onSubmit({
      preferences: [],
      favorite_color: '',
    })
    navigate(nextPath)
  }, [onSubmit, nextPath])

  const initialFormValues = useMemo(
    () => ({
      ...preferences.reduce(
        (acc, { name }, index) => ({ ...acc, [`preferences[${index}]`]: name }),
        {}
      ),
      favorite_color,
    }),
    [preferences, favorite_color]
  )

  return (
    <Slide title="Preferences">
      <MadLibInputs
        initialValues={initialFormValues}
        inputs={inputsProperties}
        structure={(el) => (
          <p className="h2 text-brand-info text-bold">
            {firstName} loves {el['preferences[0]']} and {el['preferences[1]']}
            <br />
            They really need {el['preferences[2]']}
            <br />
            Their favorite color is {el.favorite_color}
            <br />
            Please no {el['preferences[3]']}
          </p>
        )}
        onValuesChange={handleValuesChange}
      />
      <p className="xsmall ptl phl text-muted text-center">
        Answers appear on your wishlist. You can edit them at any time.
      </p>
      <InfoButton
        pill
        className="mtl"
        disabled={!formValues?.preferences?.length}
        style={{ minWidth: 230 }}
        onClick={handleNext}
      >
        Next
      </InfoButton>
      <LinkButton onClick={handleSkip}>Skip</LinkButton>
    </Slide>
  )
}
